//Imports
import $ from 'jquery';
import { getCurrentCart, ON_CART_FINISHED, pictureGenerate, handlize, generateIcon, escapeString, cartAdd, updateCart, changeCart, ON_CART_PENDING } from '@process-creative/slate-theme-tools';
import { CART_TEMPLATE_PRODUCTS } from '../../templates/cart';
import { fetchCountry, fetchGeo, geoIsFetching } from '../../tools/Geo';

//Attributes

//Selectors
const SELECTOR_DATA = '[data-free-gift]';
const FREE_GIFT_CONTAINER = '[data-free-gift-container]';

export const PROPERTY_FREE_GIFT = '_free_gift';
const FREE_GIFT_ADD = '[data-free-gift-add]';
const VARIANT_ITEM = '[data-variant-id]';
const CART_MESSAGE = '[data-cart-message]';
const CP = "c-free-gift";
const THUMBNAIL_IMAGE = '[data-block-image]';
const VARIANT_BUTTON = '[data-free-gift-variant-button]';
const COLOUR_DESC = '[data-desc-colour]';

const FREE_GIFT_TYPE_AUTO = 'automatic';
const FREE_GIFT_TYPE_MANUAL = 'manual';


// Cache ///////////////////////////////////////////////////////////////////////

/** Holds the cache of the free gift data. */
let freeGiftDataCache = null;

// States //////////////////////////////////////////////////////////////////////

/** True while the cart is pending */
let freeGiftCorrecting = false; 

// Methods //////////////////////////////////////////////////////////////////////
/**
 * Returns the Free Gift Data pulled from the Section
 * @returns Free Gift Data parsed, or null if an error occured.
 */
export const freeGiftGetData = () => {
  // Read Cache
  if(freeGiftDataCache) return freeGiftDataCache;

  try {
    // Get Element(s)
    let element = $(SELECTOR_DATA);
    if(!element || !element.length) return null;
    
    // Parse JSON
    let data =  JSON.parse($(SELECTOR_DATA).html());

    if(!data.products.length) return;

    // Map into usable format.
    data.blocks.forEach((block,id) => {
      block.handle = data.products[id].handle;
      block.id = id;

      block.product = data.products.find(p => p.handle == block.handle);
      if(!block.product.variants) return;

      block.variants = block.product.variants;
    });

    // Cache and return
    return freeGiftDataCache = data;
  } catch(e) {
    console.error(e);
    return null;
  }
}

/**
 * Checks if a Cart's Line Item is a Free Gift, or not. This won't check pricing
 * or eligibility.
 * @param item Line Item from a Cart or Checkout Object.
 * @returns True if the item is a free gift.
 */
export const freeGiftIsLineGift = (item) => {
  if(!item || !item.properties) return false;
  if(!item.properties[PROPERTY_FREE_GIFT]) return false;
  return true;
}

/**
 * Returns all of the free gift line items from the cart state.
 * @param cart (Optional) Cart State, otherwise current cart state will be used.
 * @returns Array of line items that are free gift(s) within the cart.
 */
export const freeGiftGetCartGiftLines = (cart) => {
  if(!cart) cart = getCurrentCart();
  return cart.items.filter(item => freeGiftIsLineGift(item));
}

/**
 * Checks if a given cart state is eligible for a free gift (block setting).
 * @param block Free Gift Data Block.
 * @param cart Cart state to compare against.
 * @returns True if eligible, otherwise false.
 */
export const freeGiftIsCartEligible = (block, cart) => {

  return (
    ( block.minValue ? cart.items_subtotal_price >= block.minValue * 100 : true ) && 
    ( block.maxValue ? cart.items_subtotal_price <= block.maxValue * 100 : true )
  ) && !freeGiftGeoBlocked();
}

/**
 * Adds variants to the cart as a free gift.
 * @param variantIds Array of Variant IDs to add.
 * @returns A promise that will resolve when added to cart.
 */
export const freeGiftsAddToCart = (variantIds, freeGiftBlockID) => {
  const ids = Array.isArray(variantIds) ? variantIds : [ variantIds ];
  return cartAdd({
    items: ids.map((id,i) => ({
      id, quantity: 1,
      properties: {
        [PROPERTY_FREE_GIFT]: 'true',
        'free_gift_block': freeGiftBlockID
      }
    }))
  });
};

/**
 * Removes the all free gifts from cart.
 * @returns A Promise that resolves when all free gifts have been removed.
 */
export const freeGiftRemoveAllFromCart = () => {
  // Get the current cart state
  const cart = getCurrentCart();

  // Find free gift line keys.
  const matching = freeGiftGetCartGiftLines()
  if(!matching.length) return;
  
  // Remove from cart
  return changeCart(matching.map(item => {
    return { key: item.key, quantity: 0 };
  }));
}

/**
 * Check the state of the free gift block to determine if it should qualify to
 * be automatically added to cart.
 * @returns True if the free gift should automatically be added to cart.
 */
export const freeGiftShouldAutoAdd = block => {
  return block.triggerType === FREE_GIFT_TYPE_AUTO;
}

/**
 * Returns a list of restricted countries, or false if not restricting by
 * country currently.
 * @returns String array of restricted countries, or false if none.
 */
export const freeGiftGeoIsRestricted = () => {
  const data = freeGiftGetData();
  if(!data || !data.settings || !data.settings.allowedCountries) return false;
  const countries = data.settings.allowedCountries.split(',').map(n => n.toLowerCase().trim());
  if(!countries.length) return false;
  return countries;
}

/**
 * Checks the list of restricted countries and compares against the users' 
 * country.
 * @returns True if the user is able to receive a free gift, otherwise false.
 */
export const freeGiftGeoBlocked = () => {
  if(freeGiftGeoPending()) return false;
  const restricted = freeGiftGeoIsRestricted();
  if(!restricted) return false;
  const country = fetchCountry();
  if(!country) return false;
  if(restricted.some(r => r === country)) return false;
  return true;
}

/**
 * @returns True if the geo state is pending for free gifts, otherwise false.
 */
export const freeGiftGeoPending = () => {
  if(!freeGiftGeoIsRestricted()) return false;
  if(geoIsFetching()) return true;
  return false;
}

/**
 * Checks the qualifying blocks and the cart state, and removes and/or adds the
 * necessary cart items.
 */
export const freeGiftCorrectCart = async () => {
  if(freeGiftCorrecting) return;
  if(freeGiftGeoPending()) return;

  freeGiftCorrecting = true;

  // Fetch all of the blocks.
  const cart = getCurrentCart();
  const data = freeGiftGetData();
  const currentGiftLines = freeGiftGetCartGiftLines();
  if (!data) return;

  let variantsToAdd = [];
  let linesToRemove = [];
  
  for(let i = 0; i < data.blocks.length; i++) {
    const block = data.blocks[i];
    if (!block.handle) return;

    // Get the list of available variants for this block.
    let variants = block.variants.filter(n => n);
    if(!variants || !variants.length) variants = block.product.variants;
    variants = variants.filter(v => v.available);
    if(!variants || !variants.length) continue;

    // Determine eligibility and whether or not already taking advantage of.
    const eligible = freeGiftIsCartEligible(block, cart);
    const inCart = currentGiftLines.find(line => (
      variants.some(variant => variant && variant.id === line.variant_id)
    ));

    // Correct state
    if(eligible) {
      if(inCart) {
        if(inCart.quantity === 1) continue;// If there's not 1 we correct it.
        variantsToAdd.push(inCart.variant_id);
        linesToRemove.push(inCart);
        continue;
      }
      if(!freeGiftShouldAutoAdd(block)) continue;// Don't add for non-auto add 
      variantsToAdd.push(variants.find(v => v).id);
      continue;
    }
    
    if(!inCart) continue;
    linesToRemove.push(inCart);
  }
  
  // If an item is to both be removed and added then do nothing
  linesToRemove.forEach((line,ri) => {
    if(line.quantity !== 1) return;// Only do this if there's only 1 in cart.
    const ai = variantsToAdd.indexOf(line.variant_id);
    if(ai === -1) return;
    linesToRemove[ri] = null;
    variantsToAdd[ai] = null;
  });
  
  linesToRemove = linesToRemove.filter(line => line);
  variantsToAdd = variantsToAdd.filter(variant => variant);

  try {
    // Start by removing the lines by key
    if(linesToRemove.length) {
      await changeCart(linesToRemove.map(line => ({
        key: line.key, quantity: 0
      })));
    }

    // Now add the lines
    if(variantsToAdd.length) {
      await freeGiftsAddToCart(variantsToAdd);
      setTimeout(() => freeGiftCorrectCart(), 1000);
    }
  } catch(e) {
    console.error(e);
  }

  freeGiftCorrecting = false;
}

export const freeGiftIsPending = () => freeGiftCorrecting || freeGiftGeoPending();

export const addGift = async (e) => {
  let vID = $(e.currentTarget).closest(FREE_GIFT_CONTAINER).find('.is-selected'+VARIANT_ITEM).attr('data-variant-id');
  let freeGiftBlockID = $(e.currentTarget).closest(FREE_GIFT_CONTAINER).attr('data-block-index');
  if (!vID) return;

  await freeGiftsAddToCart([ vID ], freeGiftBlockID);
  $(document).find(FREE_GIFT_CONTAINER).remove();
}

export const drawGift = () => {
  const data = freeGiftGetData();
  const freeGiftLines = freeGiftGetCartGiftLines();
  const cart = getCurrentCart();
  if(!data) return;

  // Hide free gift if already ineligible
  $(document).find(FREE_GIFT_CONTAINER).remove();
  $(document).find(CART_MESSAGE).removeClass('is-hidden');

  data.blocks.forEach((block,i) => {
    if(!block.product) return;
    if(block.triggerType !== FREE_GIFT_TYPE_MANUAL) return; // Only render manual
    if(!freeGiftIsCartEligible(block, cart)) return;
    if(freeGiftLines.find(line => line.properties.free_gift_block == i)) return;

    const blockVariants = block.variants.filter(v => typeof v !== 'undefined' );
    const variants = blockVariants && blockVariants.length ? blockVariants : block.product.variants;

    if(freeGiftLines.some(line => variants.some(v => line.variant_id === v.id))) {
      return;
    }

    let product = block.product;
    let url = `/products/${product.handle}`;
    let img = product.featured_image;


    let html =  `
      <div class="${CP}" 
        data-free-gift-container
        data-item="${product.id}"
        data-block-index="${i}"
      >
        <div class="${CP}__title-container">
          <span class="${CP}__title ${CP}__title--colour">${Language.strings['cart.freegift.cart_heading_blue']}</span>
          <span class="${CP}__title">${Language.strings['cart.freegift.cart_heading']}</span>
        </div>
        <div class="${CP}__item-container">
          <div class="${CP}__image">
            <a href="${url}" class="${CP}__image-link" data-block-image>
              ${pictureGenerate({ src: img, srcSize: 200, class:`${CP}__image-img`})}
            </a>
          </div>
          ${getSwatchHTML(block)}
        </div>
      </div>
    `;

    let freeGift = $(html);
    $(document).find(FREE_GIFT_CONTAINER).remove();
    freeGift.insertBefore(CART_TEMPLATE_PRODUCTS);
    $(document).find(CART_MESSAGE).addClass('is-hidden');
  });
}

export const getSwatchHTML = (block) => {
  if(!block) return;
  let product = block.product;

  const blockVariants = block.variants.filter(v => typeof v !== 'undefined' );
  const variants = blockVariants && blockVariants.length ? blockVariants : product.variants;

  let index = product.options.findIndex(e => {
    return handlize(e) === 'color' || handlize(e)=== 'colour'
  });
  let url = `/products/${block.handle}`;
  let title = product.title;

  let isDisabled = !$(FREE_GIFT_CONTAINER).find('.is-selected'+VARIANT_ITEM).attr('data-variant-id');

  return `
    <a href="${url}" class="${CP}__content-link">
      <span class="${CP}__content-title">${title}</span>
      <div class="${CP}__content-variant"> 
        <p class="${CP}__content-variant-item" 
        > ${product.options[index]}: <span data-desc-colour></span></p>
      </div>
    </a>
    <div class="${CP}__swatches">
      <div class="o-swatches__swatch has-js is-color">
        <div class="o-swatches__swatch-options">
          ${variants.length < 2 ?
            `<button type="button"
              class="o-swatches__swatch-option s-swatch--${handlize(variants.find(v=>v).options[index])} is-selected"
              data-value="${variants.find(v=>v).options[index]}"
              data-variant-id="${variants.find(v=>v).id}"
              data-free-gift-variant-button
            >
              <span class="o-swatches__swatch-option--soldout">
                ${generateIcon('close')}
              </span>
            </button>`:

            variants.reduce((p,v) => {
              let value = v.options[index];
              return `${p}<button type="button"
                class="o-swatches__swatch-option s-swatch--${handlize(value)}"
                data-value="${value}"
                data-variant-id="${v.id}"
                data-free-gift-variant-button
              >
                <span class="o-swatches__swatch-option--soldout">
                  ${generateIcon('close')}
                </span>
              </button>`;
            },'')
          }
        </div>
      </div>
      <button type="button" class="${CP}__content-add o-btn is-tertiary ${isDisabled&&variants.length > 1?'is-disabled':''}" ${isDisabled&&variants.length > 1?'disabled':''}
        data-free-gift-add>${Language.strings['cart.freegift.btn']}
      </button>
    </div>
  `;
}

export const selectGiftVariant = (e) => {
  $(document).find(VARIANT_BUTTON).removeClass('is-selected');
  let btn = $(e.currentTarget);
  let variantId = btn.attr('data-variant-id');
  let variantValue = btn.attr('data-value');
  let blockElement = btn.closest(FREE_GIFT_CONTAINER);
  let block = freeGiftGetData().blocks[parseInt(blockElement.attr('data-block-index'))];

  let blockVaraints = block.variants.filter(v => typeof v !== 'undefined' );

  let variant = blockVaraints.find(v => v.id == variantId);
  let img = block.product.featured_image;
  if(variant.featured_image && variant.featured_image.src) {
    img = variant.featured_image.src;
  }

  blockElement.find(THUMBNAIL_IMAGE).html(pictureGenerate({src:img, srcSize: 500}));
  blockElement.find(COLOUR_DESC).html(variantValue);

  btn.addClass('is-selected');
  btn.closest(FREE_GIFT_CONTAINER).find(FREE_GIFT_ADD).removeClass('is-disabled');
  btn.closest(FREE_GIFT_CONTAINER).find(FREE_GIFT_ADD).removeAttr('disabled');
}

//Event Listeners
$(document).on(ON_CART_FINISHED, () => {
  freeGiftCorrectCart();
  drawGift();
});
$(document).ready(() => {
  freeGiftCorrectCart();
  drawGift();
  setTimeout(() => drawGift(), 1000);
});
drawGift();
freeGiftCorrectCart();

fetchGeo(() => freeGiftCorrectCart());

$(document).on('click', FREE_GIFT_ADD, (e) => addGift(e));
$(document).on('click', VARIANT_BUTTON, (e) => selectGiftVariant(e));